import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SeasonLink as Link } from "../../components/Seasons/SeasonLink";
import styles from './resultPage.module.css';
import avatarPlaceholder from '../../assets/icons/avatar.svg';
import { getSearchUserProfile } from '../../api/search';
import { buildDisplayName } from '../../utils/userDisplay';
import { canReceiveYufunts, getRoleLabel, participatesInRating } from '../../utils/roles';
import telegramIcon from '../../assets/icons/contacts/telegram.svg';
import vkIcon from '../../assets/icons/contacts/vk.svg';
import maxIcon from '../../assets/icons/contacts/max.svg';
import { buildContactLink } from '../../utils/contactLinks';

export function UserResultPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getSearchUserProfile(Number(userId));
        setProfile(response);
      } catch (err) {
        setError(err.message || 'Не удалось загрузить профиль пользователя.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [userId]);

  if (loading) return <p className={styles.note}>Загружаем профиль...</p>;
  if (error) return <p className={styles.error}>{error}</p>;
  if (!profile) return <p className={styles.note}>Профиль не найден.</p>;
  const contactsVisible = Boolean(profile.show_contacts);
  const telegramLink = buildContactLink('telegram', profile.telegram);
  const vkLink = buildContactLink('vk', profile.vk);
  const maxLink = buildContactLink('max', profile.max_contact);
  const websiteLink = buildContactLink('website', profile.website);
  const emailLink = buildContactLink('email', profile.email);
  const showRatingMeta = participatesInRating(profile.role);
  const showBalanceMeta = canReceiveYufunts(profile.role);
  const returnQuery = (searchParams.get('q') || profile.login || '').trim();
  const returnSkill = (searchParams.get('skill') || '').trim();
  const backHref = `/search?q=${encodeURIComponent(returnQuery)}${returnSkill ? `&skill=${encodeURIComponent(returnSkill)}` : ''}`;
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(backHref);
  };

  return (
    <section className={styles.page}>
      <header className={styles.head}>
        <button type="button" className={styles.backLink} onClick={handleBack}>
          Вернуться назад
        </button>
        <h1 className={styles.title}>{buildDisplayName(profile)}</h1>
        <p className={styles.subtitle}>@{profile.login}</p>
      </header>

      <article className={styles.heroCard}>
        <img
          className={styles.avatar}
          src={profile.avatar_url || avatarPlaceholder}
          alt=""
          onError={(event) => {
            event.currentTarget.src = avatarPlaceholder;
          }}
        />
        <div className={styles.heroBody}>
          <div className={styles.chips}>
            <span className={styles.chip}>{getRoleLabel(profile.role)}</span>
            {profile.want_team ? <span className={styles.chip}>Хочу в команду</span> : null}
            {showBalanceMeta ? <span className={styles.chip}>Баланс: {profile.balance}</span> : null}
            {showRatingMeta && profile.rank_position ? <span className={styles.chip}>Место #{profile.rank_position}</span> : null}
          </div>
          <div className={styles.fields}>
            <div className={styles.field}>
              <span>Email</span>
              <strong>
                {contactsVisible && emailLink ? (
                  <a href={emailLink.href} className={styles.contactLink}>
                    {emailLink.label}
                  </a>
                ) : 'Скрыто'}
              </strong>
            </div>
            <div className={styles.field}><span>Группа</span><strong>{profile.study_group || '—'}</strong></div>
            <div className={styles.field}><span>Курс</span><strong>{profile.course_number || '—'}</strong></div>
            <div className={styles.field}>
              <span>Специальность</span>
              <strong>{profile.faculty || profile.institute || '—'}</strong>
            </div>
            <div className={styles.field}><span>Город</span><strong>{profile.city || '—'}</strong></div>
            <div className={styles.field}>
              <span>Telegram</span>
              <strong>
                {contactsVisible && telegramLink ? (
                  <a href={telegramLink.href} className={styles.contactLink} target="_blank" rel="noreferrer">
                    <img src={telegramIcon} alt="" />
                    {telegramLink.label}
                  </a>
                ) : 'Скрыто'}
              </strong>
            </div>
            <div className={styles.field}>
              <span>VK</span>
              <strong>
                {contactsVisible && vkLink ? (
                  <a href={vkLink.href} className={styles.contactLink} target="_blank" rel="noreferrer">
                    <img src={vkIcon} alt="" />
                    {vkLink.label}
                  </a>
                ) : 'Скрыто'}
              </strong>
            </div>
            <div className={styles.field}>
              <span>MAX</span>
              <strong>
                {contactsVisible && maxLink ? (
                  <a href={maxLink.href} className={styles.contactLink} target="_blank" rel="noreferrer">
                    <img src={maxIcon} alt="" />
                    {maxLink.label}
                  </a>
                ) : 'Скрыто'}
              </strong>
            </div>
            <div className={styles.field}>
              <span>Сайт</span>
              <strong>
                {contactsVisible && websiteLink ? (
                  <a href={websiteLink.href} className={styles.contactLink} target="_blank" rel="noreferrer">
                    {websiteLink.label}
                  </a>
                ) : 'Скрыто'}
              </strong>
            </div>
          </div>
          {profile.bio ? (
            <div className={styles.field}>
              <span>О себе</span>
              <strong>{profile.bio}</strong>
            </div>
          ) : null}
          {!contactsVisible ? (
            <p className={styles.note}>Контакты скрыты владельцем профиля.</p>
          ) : null}
          {profile.skills?.length ? (
            <div className={styles.chips}>
              {profile.skills.map((skill) => (
                <span key={skill} className={styles.chip}>#{skill}</span>
              ))}
            </div>
          ) : null}
        </div>
      </article>

      <article className={styles.block}>
        <h2 className={styles.blockTitle}>Команды участника</h2>
        {!profile.teams?.length ? (
          <p className={styles.note}>Пользователь пока не состоит в командах.</p>
        ) : (
          <div className={styles.cardsGrid}>
            {profile.teams.map((team) => (
              <Link key={team.team_id} to={`/search/teams/${team.team_id}`} className={styles.miniCard}>
                <img
                  className={styles.miniAvatar}
                  src={team.avatar_url || avatarPlaceholder}
                  alt=""
                  onError={(event) => {
                    event.currentTarget.src = avatarPlaceholder;
                  }}
                />
                <div>
                  <p className={styles.miniTitle}>{team.name}</p>
                  <p className={styles.miniMeta}>
                    #{team.rank_position} · {team.member_count} участников
                  </p>
                  <p className={styles.miniMeta}>
                    Статус в команде: {team.system_role}{team.custom_role ? ` (${team.custom_role})` : ''}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </article>
    </section>
  );
}
